import React, { useRef } from 'react';

interface WarrantyCertificateProps {
  agreement: any;
  equipment: any;
  homeowner: any;
  contractor: any;
  property: any;
  product: any;
}

const WarrantyCertificate: React.FC<WarrantyCertificateProps> = ({
  agreement,
  equipment,
  homeowner,
  contractor,
  property,
  product
}) => {
  const certificateRef = useRef<HTMLDivElement>(null);

  const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`;
  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const downloadPDF = async () => {
    // This would require a PDF generation library like jsPDF or Puppeteer
    // For now, we'll provide a print option
    window.print();
  };

  const downloadImage = async () => {
    if (!certificateRef.current) return;

    try {
      // Use html2canvas to convert the certificate to an image
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2,
        backgroundColor: '#0f172a',
        width: 800,
        height: 600
      });

      const link = document.createElement('a');
      link.download = `warranty-certificate-${agreement.agreement_number}.png`;
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error generating image:', error);
      alert('Error generating certificate image');
    }
  };

  return (
    <div className="space-y-6">
      {/* Download Actions */}
      <div className="flex justify-end space-x-3">
        <button
          onClick={downloadImage}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm"
        >
          📥 Download Image
        </button>
        <button
          onClick={downloadPDF}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm"
        >
          📄 Print/PDF
        </button>
      </div>

      {/* Certificate */}
      <div
        ref={certificateRef}
        className="bg-white text-gray-900 p-8 rounded-lg shadow-2xl max-w-4xl mx-auto"
        style={{ aspectRatio: '4/3' }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-3xl font-bold text-blue-900 mb-2">
            🛡️ WARRANTY CERTIFICATE
          </div>
          <div className="text-lg text-gray-600 mb-4">
            Extended HVAC Labor Warranty
          </div>
          <div className="w-32 h-1 bg-blue-600 mx-auto"></div>
        </div>

        {/* Certificate Number and Status */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="text-sm text-gray-500">Certificate Number</div>
            <div className="text-xl font-mono font-bold text-blue-900">
              {agreement.agreement_number}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Status</div>
            <div className={`text-lg font-semibold ${
              agreement.status === 'active' ? 'text-green-600' : 'text-yellow-600'
            }`}>
              {agreement.status.toUpperCase()}
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Equipment Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-bold text-gray-800 mb-3 border-b border-gray-200 pb-2">
              📱 COVERED EQUIPMENT
            </h3>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-600">Manufacturer:</span>
                <span className="ml-2 font-semibold">{equipment?.manufacturer?.name}</span>
              </div>
              <div>
                <span className="text-gray-600">Model:</span>
                <span className="ml-2 font-semibold">{equipment?.model_number}</span>
              </div>
              <div>
                <span className="text-gray-600">Serial Number:</span>
                <span className="ml-2 font-mono font-semibold">{equipment?.serial_number}</span>
              </div>
              <div>
                <span className="text-gray-600">Install Date:</span>
                <span className="ml-2">{formatDate(equipment?.install_date)}</span>
              </div>
              {equipment?.ahri_reference && (
                <div>
                  <span className="text-gray-600">AHRI Reference:</span>
                  <span className="ml-2 font-mono">{equipment.ahri_reference}</span>
                </div>
              )}
            </div>
          </div>

          {/* Property Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-bold text-gray-800 mb-3 border-b border-gray-200 pb-2">
              🏠 SERVICE LOCATION
            </h3>
            <div className="space-y-1 text-sm">
              <div className="font-semibold">{property?.street}</div>
              <div>{property?.city}, {property?.state} {property?.zip}</div>
              {property?.county && <div>County: {property.county}</div>}
            </div>
          </div>

          {/* Homeowner Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-bold text-gray-800 mb-3 border-b border-gray-200 pb-2">
              👤 CERTIFICATE HOLDER
            </h3>
            <div className="space-y-1 text-sm">
              <div className="font-semibold">
                {homeowner?.first_name} {homeowner?.last_name}
              </div>
              <div>{homeowner?.email}</div>
              <div>{homeowner?.phone}</div>
            </div>
          </div>

          {/* Contractor Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-bold text-gray-800 mb-3 border-b border-gray-200 pb-2">
              🔧 INSTALLING CONTRACTOR
            </h3>
            <div className="space-y-1 text-sm">
              <div className="font-semibold">{contractor?.legal_name}</div>
              {contractor?.dba && <div>DBA: {contractor.dba}</div>}
              <div>License: {contractor?.license_number}</div>
            </div>
          </div>
        </div>

        {/* Coverage Details */}
        <div className="bg-blue-50 p-4 rounded-lg mb-6">
          <h3 className="font-bold text-gray-800 mb-3 border-b border-gray-200 pb-2">
            🛡️ COVERAGE DETAILS
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Product:</span>
              <span className="ml-2 font-semibold">{product?.name}</span>
            </div>
            <div>
              <span className="text-gray-600">Term:</span>
              <span className="ml-2 font-semibold">{agreement.term_months} months</span>
            </div>
            <div>
              <span className="text-gray-600">Coverage Period:</span>
              <span className="ml-2">{formatDate(agreement.start_date)} - {formatDate(agreement.end_date)}</span>
            </div>
            <div>
              <span className="text-gray-600">Premium Paid:</span>
              <span className="ml-2 font-semibold">{formatCurrency(agreement.premium_cents)}</span>
            </div>
            <div>
              <span className="text-gray-600">Deductible:</span>
              <span className="ml-2">{formatCurrency(agreement.deductible_cents)}</span>
            </div>
            <div>
              <span className="text-gray-600">Per-Repair Limit:</span>
              <span className="ml-2">{formatCurrency(agreement.per_repair_cap_cents)}</span>
            </div>
            <div>
              <span className="text-gray-600">Aggregate Limit:</span>
              <span className="ml-2">{formatCurrency(agreement.aggregate_cap_cents)}</span>
            </div>
            <div>
              <span className="text-gray-600">Labor Rate:</span>
              <span className="ml-2">{formatCurrency(agreement.labor_rate_band)}/hour</span>
            </div>
          </div>
        </div>

        {/* Important Notes */}
        <div className="bg-yellow-50 p-4 rounded-lg mb-6 text-xs">
          <h4 className="font-bold text-gray-800 mb-2">⚠️ IMPORTANT COVERAGE NOTES</h4>
          <ul className="space-y-1 text-gray-700">
            <li>• This warranty covers labor costs for HVAC system repairs as outlined in the warranty agreement</li>
            <li>• Coverage is subject to terms, conditions, and exclusions detailed in the warranty contract</li>
            <li>• Service must be performed by licensed HVAC contractors</li>
            <li>• Pre-authorization may be required for certain repairs</li>
            <li>• This certificate is valid only when warranty premiums are current</li>
          </ul>
        </div>

        {/* Footer */}
        <div className="text-center pt-4 border-t border-gray-300">
          <div className="text-xs text-gray-500 mb-2">
            Certificate generated on {new Date().toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
          <div className="text-sm font-semibold text-blue-900">
            RahsMatch Warranty Services | Florida-Licensed Warranty Provider
          </div>
          <div className="text-xs text-gray-500">
            For claims and service: https://rightawayhome.com/admin/warranty/coverage
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarrantyCertificate;